/* ------------------------------------------------------------------
 *  CourseProgress — Édition éditoriale
 *  Hairlines, eyebrows, gold accent pour modules complétés.
 * ------------------------------------------------------------------ */
import { CheckCircle, Circle, Lock, ArrowUpRight } from "lucide-react"

export default function CourseProgress({
  modules,
  currentModuleId,
  onModuleSelect,
  completedModules = new Set(),
  getModuleAccess,
  checkAccess,
  courseId,
}) {
  const totalModules = modules.length
  const completedCount = completedModules.size
  const progressPercentage =
    totalModules > 0 ? (completedCount / totalModules) * 100 : 0

  return (
    <div className="bg-white border border-[#D5DAE3] p-6 sm:p-8 mb-6">
      {/* Header */}
      <div className="mb-6">
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
          Programme
        </span>
        <h3 className="font-display text-2xl text-[#203a8a] mt-1 leading-tight">
          Progression du cours
        </h3>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-end justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
            Modules terminés
          </span>
          <span className="font-display text-xl text-[#203a8a]">
            {completedCount}
            <span className="text-[#1B2240]/40 text-sm">/{totalModules}</span>
          </span>
        </div>
        <div className="w-full bg-[#F1F3F8] h-1.5 overflow-hidden">
          <div
            className="bg-[#E8C547] h-1.5 transition-all duration-700"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Modules list — hairline divided */}
      <ul className="divide-y divide-[#D5DAE3] border-y border-[#D5DAE3]">
        {modules.map((module, index) => {
          const isAccessible = checkAccess ? checkAccess(module.id) : true
          const isLocked = !isAccessible
          const isCurrent = module.id === currentModuleId
          const isDone = completedModules.has(module.id)
          const moduleAccess = getModuleAccess ? getModuleAccess(module.id) : null

          const handleClick = async (e) => {
            e.preventDefault()
            if (isLocked) return
            if (onModuleSelect) {
              await onModuleSelect(module.id)
            } else if (courseId) {
              window.location.href = `/courses/${courseId}/modules/${module.id}`
            }
          }

          return (
            <li key={module.id}>
              <button
                onClick={handleClick}
                disabled={isLocked}
                className={`w-full text-left py-4 group flex items-start gap-4 transition-colors ${
                  isLocked
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-[#FAFBFD]"
                } ${isCurrent ? "bg-[#F1F3F8]" : ""}`}
              >
                {/* Numéro Fraunces */}
                <div className="flex-shrink-0 w-12 text-center pt-0.5">
                  <span
                    className={`font-display text-2xl leading-none ${
                      isDone
                        ? "text-[#E8C547]"
                        : isCurrent
                        ? "text-[#203a8a]"
                        : "text-[#1B2240]/30"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Status icon */}
                <div className="flex-shrink-0 pt-1">
                  {isDone ? (
                    <CheckCircle className="w-4 h-4 text-[#E8C547]" />
                  ) : isLocked ? (
                    <Lock className="w-4 h-4 text-[#9CA3AF]" />
                  ) : (
                    <Circle className="w-4 h-4 text-[#203a8a]/40" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pr-3">
                  <p
                    className={`font-display text-lg leading-tight ${
                      isCurrent ? "text-[#203a8a]" : "text-[#1B2240]"
                    }`}
                  >
                    {module.title}
                    {isLocked && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF] font-sans">
                        · Verrouillé
                      </span>
                    )}
                  </p>
                  {module.description && (
                    <p className="text-sm text-[#1B2240]/60 mt-1 line-clamp-2">
                      {module.description}
                    </p>
                  )}
                  {isLocked && moduleAccess?.previousModule && (
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF] mt-2">
                      Validez « {moduleAccess.previousModule.title} » pour débloquer
                    </p>
                  )}
                </div>

                {/* Right side */}
                <div className="flex-shrink-0 flex items-center gap-3 pt-1">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF] hidden sm:inline">
                    {module._count?.blocks || 0} blocs
                  </span>
                  {!isLocked && (
                    <ArrowUpRight
                      className={`w-4 h-4 transition-all duration-500 ${
                        isCurrent
                          ? "text-[#E8C547] translate-x-0"
                          : "text-[#203a8a]/40 group-hover:text-[#203a8a] group-hover:translate-x-1 group-hover:-translate-y-1"
                      }`}
                    />
                  )}
                </div>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
